import * as ft from 'funtypes'
import { OperatorSlug } from '../domain/operators.js'
import { NonEmptyString, objectWith, parseOrThrow } from '../validation.js'
import operatorOverridesJson from './operatorOverrides.json' with { type: 'json' }

/**
 * Hand-assigned operators for standalone GE sites - see `./places.ts` - that the
 * scrape cannot identify on its own.
 *
 * {@link geOperatorSlug} in `./naming.ts` reads the operator off an EVSE id's
 * eMI3 prefix, which is the only signal a standalone site carries, and about a
 * third of connectors in the scrape publish no EVSE id at all. Every
 * Porsche-group dealership charger is in that third: two guns, no `evseId`,
 * nothing an automated match could use. A human who recognises the brand can
 * still say so, which is what `operatorOverrides.json` is for.
 *
 * Keyed by GoingElectric's own `siteId`, stable across re-scrapes. Guarded by
 * `points`, the connector count recorded at the time - not a full re-match, but
 * enough that a site GoingElectric has materially changed since (a charger
 * added or removed, the listing merged into another) stops applying silently
 * rather than mis-tagging whatever is there now. A stale override simply does
 * not apply; the site falls back to `geOperatorSlug`'s usual answer.
 *
 * Hand-maintained, not generated: unlike `table.generated.json` this file is
 * not a function of `data/goingelectric/ge-poland.json`, so nothing regenerates
 * it and it is safe to edit directly.
 */
export const GeOperatorOverride = objectWith(
  {
    /** GoingElectric's own site id, from the page URL. The key. */
    siteId: NonEmptyString,
    operatorSlug: OperatorSlug,
    /** Connector count when this was recorded. Re-checked every run. */
    points: ft.Number,
    /** Why this site belongs to the operator. Written for a reviewer. */
    reason: NonEmptyString,
    recordedAt: ft.String.withConstraint(
      (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) || 'Expected YYYY-MM-DD',
      { name: 'IsoDate' },
    ),
  },
  {
    /** Locator for a reviewer. Never matched on. */
    url: ft.String,
  },
)
export type GeOperatorOverride = ft.Static<typeof GeOperatorOverride>

export const GeOperatorOverridesFile = objectWith({ overrides: ft.Array(GeOperatorOverride) }, {})
export type GeOperatorOverridesFile = ft.Static<typeof GeOperatorOverridesFile>

const OVERRIDES_FILE = parseOrThrow(GeOperatorOverridesFile, operatorOverridesJson, {
  source: 'ge/operatorOverrides.json',
})

/** `siteId` -> override, for {@link resolveGeOperatorOverride} to look up. */
export const GE_OPERATOR_OVERRIDES: ReadonlyMap<string, GeOperatorOverride> = new Map(
  OVERRIDES_FILE.overrides.map((entry) => [entry.siteId, entry]),
)

/**
 * The hand-assigned operator for one standalone site, or `undefined` when none
 * is recorded or the recorded one has gone stale against the current scrape.
 */
export const resolveGeOperatorOverride = (
  geSiteId: string,
  place: { readonly points: readonly unknown[] },
): OperatorSlug | undefined => {
  const override = GE_OPERATOR_OVERRIDES.get(geSiteId)
  if (override === undefined) return undefined
  return override.points === place.points.length ? override.operatorSlug : undefined
}
